import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin, tap } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { Country, CountryCreate, CountryUpdate } from '@core/entities/models/country.model';
import { PaginatedList } from '@core/entities/models/paginated-list.model';
import { ApiEndpoints } from '@core/config/api.config';
import { skipGlobalLoading } from '@core/interceptors/loading-context';

/**
 * Parámetros para listar países según OpenAPI.
 */
export interface CountryListParams {
  offset?: number;
  limit?: number;
  /** Filtro de búsqueda (max 128 chars) */
  filter?: string;
  /** Campo de ordenación según OpenAPI */
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'counts.partners';
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Servicio para gestionar países.
 * Implementa las operaciones CRUD según OpenAPI spec.
 */
@Injectable({ providedIn: 'root' })
export class CountryService {
  private readonly http = inject(HttpClient);

  // ---------------------------------------------------------------------------
  // API pública - Operaciones CRUD de países
  // ---------------------------------------------------------------------------

  /**
   * Obtiene un listado paginado de países.
   * GET /countries (SearchCountriesOperation)
   *
   * @param params - Parámetros de paginación, filtrado y ordenación
   * @returns Observable con el listado paginado de países
   */
  getAll(params?: CountryListParams): Observable<PaginatedList<Country>> {
    let httpParams = new HttpParams();

    httpParams = httpParams.set('offset', (params?.offset ?? 0).toString());
    httpParams = httpParams.set('limit', (params?.limit ?? 10).toString());

    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<PaginatedList<Country>>(ApiEndpoints.countries.base, {
        params: httpParams,
        context: skipGlobalLoading(),
      })
      .pipe(
        tap({
          error: (error: any) => console.error('❌ Countries Error:', error),
        }),
      );
  }

  /**
   * Obtiene un país por su ID.
   * GET /countries/{countryId} (GetCountryOperation)
   *
   * @param id - Identificador único del país
   * @returns Observable con el país solicitado
   */
  getById(id: string): Observable<Country> {
    return this.http.get<Country>(ApiEndpoints.countries.byId(id));
  }

  /**
   * Crea un nuevo país.
   * POST /countries (CreateCountryOperation)
   *
   * @param country - Datos del país a crear
   * @returns Observable con el país creado
   */
  create(country: CountryCreate): Observable<Country> {
    return this.http.post<Country>(ApiEndpoints.countries.base, country);
  }

  /**
   * Actualiza un país existente.
   * PUT /countries/{countryId} (UpdateCountryOperation)
   *
   * @param id - Identificador del país a actualizar
   * @param country - Datos actualizados del país
   * @returns Observable con el país actualizado
   */
  update(id: string, country: CountryUpdate): Observable<Country> {
    return this.http.put<Country>(ApiEndpoints.countries.byId(id), country);
  }

  /**
   * Elimina un país.
   * DELETE /countries/{countryId} (DeleteCountryOperation)
   *
   * @param id - Identificador del país a eliminar
   * @returns Observable vacío cuando el país se elimina correctamente
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.countries.byId(id));
  }

  /**
   * Obtiene todos los países para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExport(params?: CountryListParams): Observable<Country[]> {
    return this.getAll({ ...params, offset: 0, limit: 100 }).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<PaginatedList<Country>>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getAll({ ...params, offset, limit: 100 }));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allCountries: Country[] = [];
            responses.forEach((response) => {
              allCountries.push(...response.items);
            });
            return allCountries;
          }),
        );
      }),
    );
  }
}
